/*
 * Copyright (c) 2021.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {Inject} from '@angular/core';
import {CartEventService, CartProduct, CustomProduct, Environment, ProductCustomer} from 'compob2b-libs';
import {EMPTY, Observable, of} from 'rxjs';
import {tap} from 'rxjs/operators';
import {AmountCartMethod} from '../vars';
import {BaseService} from './base.service';

export class CartBaseService extends BaseService {
  cart: CartProduct[];

  protected constructor(protected readonly cartEventService: CartEventService,
                        @Inject('environment') protected readonly environment: Environment) {
    super();
  }

  find(product: ProductCustomer): Observable<CartProduct> {
    const index = this.findIndex(product.syncUid);
    return this.cart && this.cart[index] ? of(this?.cart[index]) : EMPTY;
  }

  setCartData(cart: CartProduct[]): void {
    this.cart = cart;
    this.doNext();
  }

  replaceCartProduct() {
    this.cartEventService.nextReplaceCartProduct();
  }

  protected clear(): Observable<any> {
    this.cart.length = 0;
    return of({}).pipe(tap(_ => this.doNext()));
  }

  protected getNextIndex(): number {
    let max = 0;
    this.cart.forEach(item => max = item.id > max ? item.id : max);

    return max + 1;
  }

  protected findIndex(value: string | number) {
    if (typeof value === 'number') {
      return this.cart?.findIndex(item => item.id === value);
    }
    return this.cart?.findIndex(item => item.syncUid === value);
  }

  protected doNext(): void {
    let totalCost = 0;
    let totalCostWithoutDiscount = 0;
    let totalAmount = 0;
    const uniqueAmount = this.cart.length;
    let amount = uniqueAmount;
    let totalVolume = 0;
    let totalWeight = 0;
    let discountCostOnlyTotal = 0;

    this.cart.forEach(item => {
      if (item.discountCostOnlyTotal > 0) {
        discountCostOnlyTotal = item.discountCostOnlyTotal;
      }

      const commercePrice = item.discountPrice > 0
            && discountCostOnlyTotal === 0 ? item.discountPrice : item.commercePrice?.price;

      totalCost += item.productId == null ? item.price * item.amount : commercePrice * item.amount;
      totalCostWithoutDiscount += item.productId == null ? item.price * item.amount : item.commercePrice.price * item.amount;
      totalAmount += item.amount;
      totalWeight += item.weight * item.amount;
      totalVolume += item.width * item.height * item.length * item.amount / 1000000;
    });

    if (this.environment.amountCartMethod === AmountCartMethod.TOTAL) {
      amount = totalAmount;
    }

    this.cartEventService.nextTotalCost(totalCost);
    this.cartEventService.nextTotalCostWithoutDiscount(totalCostWithoutDiscount);
    this.cartEventService.nextDiscountTotalCost(discountCostOnlyTotal);
    this.cartEventService.nextTotalAmount(totalAmount);
    this.cartEventService.nextUniqueAmount(uniqueAmount);
    this.cartEventService.nextAmount(amount);
    this.cartEventService.nextTotalVolume(totalVolume);
    this.cartEventService.nextTotalWeight(totalWeight);
    this.cartEventService.nextCart(this.cart);
  }

  protected doNextCartProduct(cartProduct: CartProduct): void {
    this.cartEventService.nextCartProduct(cartProduct);
  }

  protected createCartProduct(product: ProductCustomer, amount: number): CartProduct {
    return {
      id: this.getNextIndex(),
      amount,
      articul: product.articul,
      header: product.header,
      manufacturerHeader: product.manufacturerHeader,
      brandHeader: product.brandHeader,
      manufacturerSeriesHeader: product.manufacturerSeriesHeader,
      unitHeader: product.unitHeader,
      price: product.commercePrice.price,
      description: product.description,
      picture: product.picture,
      productId: product.id,
      syncUid: product.syncUid,
      multiplicityOrder: product.multiplicityOrder,
      multiplicitySupplier: product.multiplicitySupplier,
      commercePrice: product.commercePrice,
      balancesOnGroupsOfWarehouses: product.balancesOnGroupsOfWarehouses,
      productStatusId: product.productStatusId,
      width: product.weight,
      height: product.height,
      length: product.length,
      weight: product.weight
    };
  }

  protected createCustomCartProduct(product: CustomProduct): CartProduct {
    return {
      id: this.getNextIndex(),
      productId: null,
      syncUid: null,
      amount: product.quantity,
      articul: product.code,
      header: product.header,
      manufacturerHeader: product.manufacturer,
      brandHeader: product.brand,
      unitHeader: product.unit,
      price: product.price,
      description: product.text,
      picture: null
    };
  }
}
